<script setup lang="ts">
import IconButtonsAndSeals from '@/components/atoms/icons/IconButtonsAndSeals.vue'
import IconContact from '@/components/atoms/icons/IconContact.vue'
import IconDesign from '@/components/atoms/icons/IconDesign.vue'
import IconDomains from '@/components/atoms/icons/IconDomains.vue'
import IconFiles from '@/components/atoms/icons/IconFiles.vue'
import IconIntegrations from '@/components/atoms/icons/IconIntegrations.vue'
import IconLinkBuilding from '@/components/atoms/icons/IconLinkBuilding.vue'
import IconRedirects from '@/components/atoms/icons/IconRedirects.vue'
import IconSeo from '@/components/atoms/icons/IconSeo.vue'
import BetaPanelHeading from '@/components/beta-dashboard/molecules/BetaPanelHeading.vue'
import BetaAdvertisingSettingsPanel from '@/components/beta-dashboard/organisms/BetaAdvertisingSettingsPanel.vue'
import BetaContactSettingsPanel from '@/components/beta-dashboard/organisms/BetaContactSettingsPanel.vue'
import BetaDomainsSettingsPanel from '@/components/beta-dashboard/organisms/BetaDomainsSettingsPanel.vue'
import BetaFilesSettingsPanel from '@/components/beta-dashboard/organisms/BetaFilesSettingsPanel.vue'
import BetaIdentitySettingsPanel from '@/components/beta-dashboard/organisms/BetaIdentitySettingsPanel.vue'
import BetaIntegrationsSettingsPanel from '@/components/beta-dashboard/organisms/BetaIntegrationsSettingsPanel.vue'
import BetaLinkBuildingSettingsPanel from '@/components/beta-dashboard/organisms/BetaLinkBuildingSettingsPanel.vue'
import BetaRedirectsSettingsPanel from '@/components/beta-dashboard/organisms/BetaRedirectsSettingsPanel.vue'
import BetaSeoSettingsPanel from '@/components/beta-dashboard/organisms/BetaSeoSettingsPanel.vue'
import type { BetaSettingsSection } from '@/types/beta-dashboard'

const props = defineProps<{
    section: BetaSettingsSection
}>()

const emit = defineEmits<{
    section: [section: BetaSettingsSection]
    confirmDeleteDomain: [domainId: number]
}>()

const sections = [
    { id: 'identity' as const, label: 'Identidade', description: 'Nome e marca', icon: IconDesign },
    { id: 'contact' as const, label: 'Contato', description: 'E-mail e telefone', icon: IconContact },
    { id: 'integrations' as const, label: 'Integrações', description: 'Canais e métricas', icon: IconIntegrations },
    { id: 'domains' as const, label: 'Domínios', description: 'Endereço do site', icon: IconDomains },
    { id: 'seo' as const, label: 'SEO', description: 'Busca e indexação', icon: IconSeo },
    { id: 'advertising' as const, label: 'Publicidade', description: 'Banners e selos', icon: IconButtonsAndSeals },
    { id: 'link-building' as const, label: 'Link building', description: 'Página de links', icon: IconLinkBuilding },
    { id: 'redirects' as const, label: 'Redirecionamentos', description: 'Encaminhar URLs', icon: IconRedirects },
    { id: 'files' as const, label: 'Arquivos', description: 'Downloads do site', icon: IconFiles },
]

</script>

<template>
    <div class="beta-settings">
        <BetaPanelHeading title="Configurações" />

        <div class="beta-settings__layout">
            <nav aria-label="Seções de configurações" class="beta-settings__nav">
                <button
                    v-for="item in sections"
                    :key="item.id"
                    type="button"
                    :class="['beta-settings__nav-item', props.section === item.id && 'is-active']"
                    @click="emit('section', item.id)"
                >
                    <component :is="item.icon" />
                    <span>
                        <strong>{{ item.label }}</strong>
                        <small>{{ item.description }}</small>
                    </span>
                </button>
            </nav>

            <section class="beta-settings__panel">
                <BetaIdentitySettingsPanel v-if="props.section === 'identity'" />

                <BetaContactSettingsPanel v-else-if="props.section === 'contact'" />

                <BetaIntegrationsSettingsPanel v-else-if="props.section === 'integrations'" />

                <BetaDomainsSettingsPanel
                    v-else-if="props.section === 'domains'"
                    @confirm-delete-domain="emit('confirmDeleteDomain', $event)"
                />

                <BetaSeoSettingsPanel v-else-if="props.section === 'seo'" />

                <BetaAdvertisingSettingsPanel v-else-if="props.section === 'advertising'" />

                <BetaLinkBuildingSettingsPanel v-else-if="props.section === 'link-building'" />

                <BetaRedirectsSettingsPanel v-else-if="props.section === 'redirects'" />

                <BetaFilesSettingsPanel v-else-if="props.section === 'files'" />
            </section>
        </div>
    </div>
</template>

<style scoped>
.beta-settings {
    display: grid;
    gap: 28px;
}

.beta-settings__layout {
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr);
    gap: 18px;
    align-items: start;
}

.beta-settings__nav {
    display: grid;
    gap: 7px;
    border: 1px solid #e0e7f1;
    border-radius: 22px;
    background: #fff;
    padding: 10px;
}

.beta-settings__nav-item {
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr);
    align-items: center;
    gap: 10px;
    border-radius: 14px;
    padding: 11px;
    color: #647791;
    text-align: left;
}

.beta-settings__nav-item:hover,
.beta-settings__nav-item.is-active {
    background: color-mix(in srgb, var(--main-color) 9%, white);
    color: var(--main-color);
}

.beta-settings__nav-item :deep(svg) {
    width: 18px;
    height: 18px;
}

.beta-settings__nav-item strong,
.beta-settings__nav-item small {
    display: block;
}

.beta-settings__nav-item strong {
    font-size: 14px;
    font-weight: 850;
}

.beta-settings__nav-item small {
    margin-top: 2px;
    color: #8a99ad;
    font-size: 10px;
}

.beta-settings__panel {
    border: 1px solid #e0e7f1;
    border-radius: 24px;
    background: #fff;
    padding: 24px;
}

@media (max-width: 900px) {
    .beta-settings__layout {
        grid-template-columns: 1fr;
    }

    .beta-settings__nav {
        display: flex;
        overflow-x: auto;
    }

    .beta-settings__nav-item {
        min-width: 150px;
    }
}

@media (max-width: 640px) {
    .beta-settings__panel {
        padding: 18px;
    }
}
</style>
